import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { map, path, pathOr, filter, propEq } from 'ramda';
import { format } from 'd3-format';
import moment from 'moment';

import { ClassProp } from 'helpers/types';
import { opacityAnimation } from 'config';

type Value = Function | string | number;
type Styles = ClassProp<typeof styles>;

export type Field = {
  label: string;
  value: Value;
  formatter?: Function;
  name?: string;
};

type XAxis = {
  key: string;
  label: string;
  type?: 'date' | 'number';
};

type Props = {
  xAxis: XAxis;
  fields: Field[];
  classes: Styles;
  payload?: any;
  active?: boolean;
  label?: string;
};

const styles = {
  customTooltip: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    padding: '1rem',
    fontSize: '0.85rem',
    borderRadius: '.250rem',
    border: '0.0625rem solid rgb(232, 232, 232)',
    boxShadow: '0 .125rem .375rem 0 rgba(0,0,0,0.05)',
  },
  field: {
    animation: `${opacityAnimation} 1s linear`,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '.250rem',
  },
  label: {
    fontWeight: 500,
    marginRight: '1rem',
  },
};

const defaultFormatter = format('$.0f');

const getPayloadByName = (name?: string, dataPayload?: any) => {
  const payloadWithDefault = pathOr({}, [0, 'payload']);

  if (name && dataPayload) {
    const filteredPayload = filter(propEq('name', name), dataPayload as any);
    return payloadWithDefault(filteredPayload);
  }

  return payloadWithDefault(dataPayload);
};

const getValue = (
  { formatter = defaultFormatter, value, name }: Field,
  dataPayload?: any,
) => {
  const payload = getPayloadByName(name, dataPayload);

  if (typeof value === 'number') {
    return formatter(value);
  }

  if (typeof value === 'function') {
    return formatter(value(payload));
  }

  return formatter(payload[value as string]);
};

const renderFields = (classes: Styles, payload?: any) =>
  map(({ label, ...rest }: Field) => (
    <div key={label} className={classes.field}>
      <div className={classes.label}>{label}: </div>
      <div>{getValue(rest as Field, payload)}</div>
    </div>
  ));

const CustomTooltip: FunctionComponent<Props> = ({
  classes,
  active,
  payload,
  xAxis,
  fields,
}) => {
  const { key, label, type } = xAxis;
  const point = path<Date | string>([0, 'payload', key], payload);

  if (active) {
    return (
      <div className={classes.customTooltip}>
        {renderFields(classes, payload)(fields)}

        <div className={classes.field}>
          <div className={classes.label}>{label}: </div>

          <div>
            {type === 'date' ? moment(point).format('MMM DD, YYYY') : point}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default withStyles(styles)(CustomTooltip);
